import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { grid, media } from "../assets/styles/gridDefines";
import deviceRangeValues from "../defines/deviceRangeValues";
import { getSearchParam } from "../getSearchParam";
import { getWebViewType } from "../getWebViewType";
import { window } from "../window";
import { DeviceInfoContext } from "./DeviceInfoContext";

const COLUMN_SPACING_VALUES = {
  large: parseInt(grid.largeColumnSpacing),
  medium: parseInt(grid.mediumColumnSpacing),
  small: parseInt(grid.smallColumnSpacing),
  xlarge: parseInt(grid.xlargeColumnSpacing),
};
const MEDIA_WIDTH_LIMITS_VALUES = {
  large: { max: media.largeMax, min: media.largeMin },
  medium: { max: media.mediumMax, min: media.mediumMin },
  small: { max: media.smallMax, min: media.smallMin },
  xlarge: { min: media.xlargeMin },
};
const NUMBER_COLUMNS_VALUES = {
  large: parseInt(grid.largeNumCols),
  medium: parseInt(grid.mediumNumCols),
  small: parseInt(grid.smallNumCols),
  xlarge: parseInt(grid.xlargeNumCols),
};
const PADDING_VALUES = {
  large: parseInt(grid.largePadding),
  medium: parseInt(grid.mediumPadding),
  small: parseInt(grid.smallPadding),
  xlarge: parseInt(grid.xlargePadding),
};
const ROW_SPACING_VALUES = {
  large: parseInt(grid.largeRowSpacing),
  medium: parseInt(grid.mediumRowSpacing),
  small: parseInt(grid.smallRowSpacing),
  xlarge: parseInt(grid.xlargeRowSpacing),
};
const MEDIA_QUERIES = [
  {
    query: `(max-width: ${deviceRangeValues.smallMax})`,
    size: "small",
  },
  {
    query: `(max-width: ${deviceRangeValues.mediumMax}) and (min-width: ${deviceRangeValues.mediumMin})`,
    size: "medium",
  },
  {
    query: `(max-width: ${deviceRangeValues.largeMax}) and (min-width: ${deviceRangeValues.largeMin})`,
    size: "large",
  },
  {
    query: `(min-width: ${deviceRangeValues.xlargeMin})`,
    size: "xlarge",
  },
];

/** Provider used to query and expose information about the current device. */

export const DeviceInfoProvider = (props) => {
  const { children, darkModeEnabled } = props;
  const [screenSize, setScreenSize] = useState(() => getActiveScreenSize(getWatchers()));
  const orientationQuery = window.matchMedia?.("(orientation: portrait)");
  const [isLandscape, setIsLandscape] = useState(!orientationQuery?.matches);
  const [isPortrait, setIsPortrait] = useState(orientationQuery?.matches);
  const [isDarkMode, setIsDarkMode] = useState(
    darkModeEnabled ? getSystemDarkModePreference()?.matches : false
  );
  const isPhysicalKeyboardPresent = window.matchMedia?.(
    "(hover: hover) and (pointer: fine)"
  )?.matches;
  const isTouchDevice = window.matchMedia?.("(hover: none), (pointer: coarse)")?.matches;
  const webViewType = getWebViewType();

  useEffect(() => {
    const handleDarkModeChange = (event) => {
      setIsDarkMode(event.matches);
    };

    if (darkModeEnabled) {
      const mediaQuery = getSystemDarkModePreference();

      mediaQuery.addEventListener("change", handleDarkModeChange);
    }

    return () => {
      if (darkModeEnabled) {
        mediaQuery.removeEventListener("change", handleDarkModeChange);
      }
    };
  }, []);

  useEffect(() => {
    const watchers = getWatchers();

    orientationQuery.addEventListener
      ? orientationQuery.addEventListener("change", handleChange)
      : orientationQuery.addListener(handleChange);
    watchers.forEach((watcher) => {
      watcher.query.addEventListener
        ? watcher.query.addEventListener("change", watcher.listener)
        : watcher.query.addListener(watcher.listener);
    });

    return () => {
      orientationQuery.removeEventListener
        ? orientationQuery.removeEventListener("change", handleChange)
        : orientationQuery.removeListener(handleChange);
      watchers.forEach((watcher) => {
        watcher.query.removeEventListener
          ? watcher.query.removeEventListener("change", watcher.listener)
          : watcher.query.removeListener(watcher.listener);
      });
    };
  }, []);

  return (
    <DeviceInfoContext.Provider value={getDeviceInfoContextValue()}>
      {children}
    </DeviceInfoContext.Provider>
  );

  function getSystemDarkModePreference() {
    return window.matchMedia?.("(prefers-color-scheme: dark)");
  }

  function handleChange(event) {
    setIsLandscape(!event?.matches);
    setIsPortrait(event?.matches);
  }

  function getActiveScreenSize(watchers) {
    return watchers.filter((watcher) => watcher.query?.matches)?.[0]?.size ?? "small";
  }

  function getWatchers() {
    return MEDIA_QUERIES.map(({ query, size }) => ({
      listener: (event) => evaluateSizeWatcher(event, size),
      query: window.matchMedia?.(query),
      size,
    }));
  }

  function evaluateSizeWatcher(event, size) {
    event?.matches && setScreenSize(size);
  }

  function getEnvironment() {
    const windowUrl = window?.location?.href;
    const isLocalEnv = process.env.NODE_ENV === "development";
    const isTestingEnv = process.env.NODE_ENV === "testing";
    const isProductionEnv = process.env.NODE_ENV === "production";
    // process.env.NODE_ENV returns "production" for our qa and dev environments hence the windowUrl checks
    const isDevEnvironment = new RegExp(`\.dev[0-9]+\.southwest`).test(windowUrl);
    const isQaEnvironment = new RegExp(`\.qa[0-9]+\.southwest`).test(windowUrl);
    let environment;

    if (isLocalEnv) {
      environment = "local";
    } else if (isTestingEnv) {
      environment = "testing";
    } else if (isDevEnvironment) {
      environment = "dev";
    } else if (isQaEnvironment) {
      environment = "qa";
    } else if (isProductionEnv && !isDevEnvironment && !isQaEnvironment) {
      environment = "production";
    }

    return environment;
  }

  function getDeviceInfoContextValue() {
    return {
      environment: getEnvironment(),
      gridColumnSpacing: getValue(COLUMN_SPACING_VALUES),
      gridNumberColumns: getValue(NUMBER_COLUMNS_VALUES),
      gridPadding: getValue(PADDING_VALUES),
      gridRowSpacing: getValue(ROW_SPACING_VALUES),
      isDarkMode,
      isInIframe: window.self !== window.top && getSearchParam("iframe") === "true",
      isLandscape,
      isLargeOrXlarge: screenSize === "large" || screenSize === "xlarge",
      isPhysicalKeyboardPresent,
      isPortrait,
      isSecureWebView: getSearchParam("forceSecureWebView") ?? false,
      isSmallOrMedium: screenSize === "small" || screenSize === "medium",
      isSwaWebView: !!webViewType,
      isTouchDevice,
      isVendorHosted: getIsVendorHosted(),
      mediaQueries: MEDIA_QUERIES,
      mediaWidthLimits: getValue(MEDIA_WIDTH_LIMITS_VALUES),
      screenSize,
      setIsDarkMode,
      webViewType,
    };
  }

  function getValue(values) {
    return values[screenSize] || 0;
  }

  function getIsVendorHosted() {
    const { hostname } = window.location;
    const environment = getEnvironment();
    const productionHostRegex = /^(www|espanol|mobile|mobile-offline)\.(southwest|swabiz)\.com$/;

    return (
      process.env.SITE === "external" &&
      environment === "production" &&
      !productionHostRegex.test(hostname)
    );
  }
};

DeviceInfoProvider.displayName = "DeviceInfoProvider";

DeviceInfoProvider.propTypes = {
  /** Content that will be rendered with context. */
  children: PropTypes.node,

  /** Array of user agent tokens to determine if web view is "typed", by the window user agent having a custom suffix. */
  userAgentTokens: PropTypes.arrayOf(PropTypes.string),
};

DeviceInfoContext.defaultProps = {
  userAgentTokens: [],
};
